import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ReviewModal from '../../../components/ReviewModal';
import { normaliseCertifications, ensureAbsoluteUrl } from '../../../utils/certifications';
import {
  getExpertReviews,
  getExpertWorkHistory,
  getClientContractWithExpert,
} from '../../../services/reviewService';

// ── Small shared UI helpers ─────────────────────────────────────────────────

const Stars = ({ rating, size = 14 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <svg
        key={n}
        viewBox="0 0 16 16"
        className={`${size === 14 ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${
          n <= Math.round(rating ?? 0) ? 'text-amber-400' : 'text-border'
        }`}
        fill="currentColor"
      >
        <path d="M8 1.5l1.75 3.5 3.875.563-2.813 2.737.664 3.863L8 10.25l-3.476 1.913.664-3.863L2.375 5.563 6.25 5z" />
      </svg>
    ))}
  </div>
);

const Stat = ({ icon, label, value }) => (
  <div className="flex flex-col items-center gap-1.5 p-3 bg-muted/50 rounded-xl text-center">
    <Icon name={icon} size={18} className="text-primary" />
    <div className="text-xl font-bold text-foreground leading-none">{value ?? '–'}</div>
    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
  </div>
);

const SectionTitle = ({ icon, children }) => (
  <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2 uppercase tracking-wide">
    <Icon name={icon} size={14} className="text-primary" />
    {children}
  </h3>
);

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// ── Main component ──────────────────────────────────────────────────────────

/**
 * ExpertProfileModal
 *
 * Props:
 *   expert          – normalized expert object from marketplace listing
 *   isOpen          – boolean
 *   onClose         – () => void
 *   onRequestQuote  – (expert) => void  — opens ContractModal
 *   currentUserId   – UUID | null       — logged-in user's auth ID
 */
const ExpertProfileModal = ({ expert, isOpen, onClose, onRequestQuote, currentUserId }) => {
  const [reviews, setReviews]                   = useState([]);
  const [workHistory, setWorkHistory]           = useState([]);
  const [reviewsLoading, setReviewsLoading]     = useState(false);
  const [historyLoading, setHistoryLoading]     = useState(false);
  const [eligibleContractId, setEligibleContractId] = useState(null);
  const [alreadyReviewed, setAlreadyReviewed]   = useState(false);
  const [reviewModalOpen, setReviewModalOpen]   = useState(false);

  useEffect(() => {
    if (!isOpen || !expert?.id) return;
    loadReviews();
    loadWorkHistory();
    if (currentUserId) checkReviewEligibility();
  }, [isOpen, expert?.id, currentUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReviews = async () => {
    setReviewsLoading(true);
    const res = await getExpertReviews(expert.id);
    setReviews(res.reviews || []);
    setReviewsLoading(false);
  };

  const loadWorkHistory = async () => {
    setHistoryLoading(true);
    const res = await getExpertWorkHistory(expert.id);
    setWorkHistory(res.history || []);
    setHistoryLoading(false);
  };

  const checkReviewEligibility = async () => {
    const res = await getClientContractWithExpert(expert.id);
    if (res.row) {
      setEligibleContractId(res.row.contract_id);
      setAlreadyReviewed(res.row.already_reviewed);
    } else {
      setEligibleContractId(null);
      setAlreadyReviewed(false);
    }
  };

  const handleReviewSubmitted = () => {
    setAlreadyReviewed(true);
    loadReviews();
    loadWorkHistory(); // refresh inline feedback
  };

  if (!isOpen || !expert) return null;

  const unavailable = expert?.availability === 'unavailable';
  const displayedReviewCount = reviews.length;
  const displayedRating =
    reviews.length > 0
      ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
      : expert?.rating ?? 0;

  // Map contract_id → review (for client name + created_at lookup)
  const reviewByContract = Object.fromEntries(
    reviews.map((r) => [r.contract_id, r])
  );

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
        role="dialog"
        aria-modal="true"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

        {/* Modal shell */}
        <div className="relative z-10 bg-card w-full sm:max-w-4xl sm:mx-4 max-h-[95vh] sm:max-h-[90vh] rounded-t-3xl sm:rounded-2xl border border-border shadow-2xl flex flex-col overflow-hidden">

          {/* Mobile drag handle */}
          <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
            <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
          </div>

          {/* Sticky header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-bold text-foreground truncate">{expert?.name}</span>
              {expert?.verified && (
                <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full flex-shrink-0">
                  <Icon name="ShieldCheck" size={10} className="text-emerald-600" />
                  Verified
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground flex-shrink-0"
              aria-label="Close"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col lg:flex-row">

              {/* ── Left sidebar ── */}
              <div className="lg:w-64 xl:w-72 flex-shrink-0 p-6 lg:border-r border-border lg:sticky lg:top-0 lg:self-start">
                <div className="flex flex-col items-center text-center">

                  {/* Avatar */}
                  <div className="relative mb-4">
                    <Image
                      src={expert?.avatar}
                      alt={expert?.avatarAlt || expert?.name}
                      className="w-24 h-24 lg:w-32 lg:h-32 rounded-2xl object-cover ring-4 ring-border"
                    />
                    {expert?.verified && (
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center border-[3px] border-card shadow-md">
                        <Icon name="Check" size={13} color="#fff" />
                      </div>
                    )}
                  </div>

                  <h2 className="text-lg font-bold text-foreground leading-tight">{expert?.name}</h2>
                  <p className="text-xs text-muted-foreground mt-1 mb-3 leading-snug">{expert?.title}</p>

                  <div className="flex items-center gap-2 mb-1">
                    <Stars rating={displayedRating} />
                    <span className="text-sm font-bold text-foreground">{displayedRating.toFixed(1)}</span>
                  </div>
                  {displayedReviewCount > 0 && (
                    <p className="text-xs text-muted-foreground mb-3">
                      {displayedReviewCount} review{displayedReviewCount !== 1 ? 's' : ''}
                    </p>
                  )}

                  <div className="text-2xl font-extrabold text-foreground mb-1">
                    ${expert?.hourlyRate ?? '–'}
                    <span className="text-sm font-normal text-muted-foreground">/hr</span>
                  </div>

                  <span
                    className={`mt-2 mb-5 text-[11px] font-semibold px-3 py-1 rounded-full ${
                      unavailable
                        ? 'bg-muted text-muted-foreground'
                        : expert?.availability === 'limited'
                        ? 'bg-amber-400/15 text-amber-600'
                        : 'bg-emerald-500/10 text-emerald-600'
                    }`}
                  >
                    {unavailable
                      ? '● Unavailable'
                      : expert?.availability === 'limited'
                      ? '● Limited Availability'
                      : '● Available Now'}
                  </span>

                  <div className="grid grid-cols-3 gap-2 w-full">
                    <Stat icon="Briefcase" label="Cases"   value={expert?.casesCompleted ?? '–'} />
                    <Stat icon="Clock"     label="Yrs Exp" value={expert?.experienceYears ?? '–'} />
                    <Stat icon="Star"      label="Rating"  value={displayedRating.toFixed(1)} />
                  </div>

                  {/* Leave Review CTA */}
                  {eligibleContractId && !alreadyReviewed && (
                    <button
                      onClick={() => setReviewModalOpen(true)}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-amber-400/40 bg-amber-400/8 text-amber-600 text-sm font-semibold hover:bg-amber-400/15 transition-colors"
                    >
                      <Icon name="Star" size={15} />
                      Leave a Review
                    </button>
                  )}
                  {eligibleContractId && alreadyReviewed && (
                    <div className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-muted text-xs text-muted-foreground">
                      <Icon name="CheckCircle" size={14} className="text-emerald-500" />
                      You've reviewed this expert
                    </div>
                  )}
                </div>
              </div>

              {/* ── Right column ── */}
              <div className="flex-1 p-6 space-y-7 min-w-0">

                {/* Bio */}
                {expert?.bio && (
                  <section>
                    <SectionTitle icon="User">About</SectionTitle>
                    <p className="text-sm text-muted-foreground leading-relaxed">{expert.bio}</p>
                  </section>
                )}

                {/* Specialties */}
                {expert?.specialties?.length > 0 && (
                  <section>
                    <SectionTitle icon="Zap">Specialties</SectionTitle>
                    <div className="flex flex-wrap gap-2">
                      {expert.specialties.map((s, i) => (
                        <span key={i} className="text-sm font-medium px-3 py-1.5 bg-primary/8 text-primary rounded-lg">
                          {s}
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Certifications */}
                {expert?.certifications?.length > 0 && (
                  <section>
                    <SectionTitle icon="Award">Certifications</SectionTitle>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {normaliseCertifications(expert.certifications).map((cert, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-primary/5 border border-primary/15 rounded-xl">
                          {cert.image_url ? (
                            <img
                              src={cert.image_url}
                              alt={cert.name}
                              className="w-8 h-8 rounded-lg object-cover flex-shrink-0 border border-border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const fb = e.target.nextElementSibling;
                                if (fb) fb.style.display = '';
                              }}
                            />
                          ) : null}
                          {/* Fallback icon — hidden when image loads, shown on error */}
                          <div
                            className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0"
                            style={{ display: cert.image_url ? 'none' : '' }}
                          >
                            <Icon name="Award" size={14} color="#fff" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-semibold text-foreground">{cert.name}</span>
                            {cert.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">{cert.description}</p>
                            )}
                            {cert.url && (
                              <a
                                href={ensureAbsoluteUrl(cert.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                              >
                                <Icon name="ExternalLink" size={10} /> Verify
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Education */}
                {expert?.education?.length > 0 && (
                  <section>
                    <SectionTitle icon="GraduationCap">Education</SectionTitle>
                    <div className="space-y-2">
                      {expert.education.map((edu, i) => (
                        <div key={i} className="p-3.5 bg-muted/40 border border-border rounded-xl">
                          <div className="font-semibold text-sm text-foreground">{edu?.degree}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {edu?.institution} · {edu?.year}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Work Experience */}
                {expert?.workExperiences?.length > 0 && (
                  <section>
                    <SectionTitle icon="Building2">Work Experience</SectionTitle>
                    <div className="overflow-hidden rounded-xl border border-border">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50 border-b border-border">
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Role</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Company</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">Period</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {expert.workExperiences.map((we, i) => (
                            <tr key={i} className="hover:bg-muted/30 transition-colors">
                              <td className="px-4 py-3">
                                <p className="font-medium text-foreground">{we.role}</p>
                                {we.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{we.description}</p>
                                )}
                              </td>
                              <td className="px-4 py-3 text-muted-foreground align-top">{we.company}</td>
                              <td className="px-4 py-3 text-muted-foreground whitespace-nowrap align-top">
                                {we.start_year}–{we.end_year ?? 'Present'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* ── Work History & Feedback ── */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <SectionTitle icon="Briefcase">
                      Work History
                      {displayedReviewCount > 0 && (
                        <span className="text-xs font-normal text-muted-foreground normal-case tracking-normal ml-1">
                          · {displayedReviewCount} review{displayedReviewCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </SectionTitle>
                  </div>

                  {historyLoading || reviewsLoading ? (
                    <div className="space-y-3">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
                      ))}
                    </div>
                  ) : workHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No completed engagements yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {workHistory.map((job) => {
                        const rev = reviewByContract[job.contract_id];
                        const isEligible = job.contract_id === eligibleContractId;

                        return (
                          <div key={job.contract_id} className="border border-border rounded-xl overflow-hidden">
                            {/* Contract header */}
                            <div className="px-4 pt-4 pb-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-foreground">{job.title}</p>
                                  {job.description && (
                                    <p className="text-xs text-muted-foreground mt-0.5">{job.description}</p>
                                  )}
                                </div>
                                <span className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-1 rounded-lg flex-shrink-0 capitalize">
                                  {job.contract_type}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                {formatDate(job.start_date)}
                                {job.completed_at ? ` – ${formatDate(job.completed_at)}` : ''}
                              </p>
                            </div>

                            {/* Feedback block */}
                            {rev ? (
                              <div className="px-4 py-3 bg-amber-400/5 border-t border-amber-400/20">
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                  <div className="flex items-center gap-2">
                                    <Stars rating={rev.rating} size={14} />
                                    <span className="text-xs font-bold text-foreground">{rev.rating}.0</span>
                                  </div>
                                  <span className="text-[11px] text-muted-foreground">{formatDate(rev.created_at)}</span>
                                </div>
                                {rev.title && (
                                  <p className="text-xs font-semibold text-foreground">{rev.title}</p>
                                )}
                                {rev.comment && (
                                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed italic">
                                    "{rev.comment}"
                                  </p>
                                )}
                                <p className="text-[11px] font-semibold text-muted-foreground mt-1.5">
                                  — {rev.client?.full_name || 'Verified Client'}
                                </p>
                              </div>
                            ) : isEligible && !alreadyReviewed ? (
                              <div className="px-4 py-3 bg-muted/30 border-t border-border flex items-center justify-between gap-3">
                                <p className="text-xs text-muted-foreground">You completed this engagement</p>
                                <button
                                  onClick={() => setReviewModalOpen(true)}
                                  className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline flex-shrink-0"
                                >
                                  <Icon name="Star" size={12} />
                                  Leave feedback
                                </button>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

              </div>
            </div>
          </div>

          {/* Sticky footer */}
          <div className="flex items-center gap-3 px-5 py-4 border-t border-border bg-muted/20 flex-shrink-0">
            <Button variant="outline" className="sm:w-28" onClick={onClose}>
              Close
            </Button>
            {onRequestQuote && (
              <Button
                variant="default"
                className="flex-1"
                disabled={unavailable}
                iconName="Send"
                iconPosition="left"
                onClick={() => { onRequestQuote(expert); }}
              >
                Send Contract Offer
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Review modal — rendered outside the profile modal scroll context */}
      <ReviewModal
        expert={{ id: expert.id, full_name: expert.name, title: expert.title, avatar_url: expert.avatar }}
        contractId={eligibleContractId}
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        onSubmitted={handleReviewSubmitted}
      />
    </>
  );
};

export default ExpertProfileModal;
